import { useEffect, useState } from "react";
import { api, pdfUrl } from "@/lib/api";
import { Plus, Pencil, Trash2, Download, X, Trash } from "lucide-react";
import { toast } from "sonner";

const rupiah = (n) => "Rp " + (Number(n || 0)).toLocaleString("id-ID");

export default function DocumentManager({ mode }) {
  const isInvoice = mode === "invoices";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = () => api.get(`/admin/${mode}`).then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, [mode]);

  const openNew = () => {
    setEditing(null);
    setForm(isInvoice
      ? { client_name: "", client_address: "", date: new Date().toISOString().slice(0, 10), due_date: "", items: [{ description: "", qty: 1, price: 0 }], tax_rate: 11, discount: 0, notes: "" }
      : { received_from: "", for_payment: "", date: new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }), amount: 0, signer_name: "", signer_title: "" });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    if (isInvoice) setForm(it);
    else setForm({ ...it, amount: it.total });
    setOpen(true);
  };

  const setLine = (i, key, val) => { const arr = [...form.items]; arr[i] = { ...arr[i], [key]: val }; setForm({ ...form, items: arr }); };
  const addLine = () => setForm({ ...form, items: [...form.items, { description: "", qty: 1, price: 0 }] });
  const delLine = (i) => setForm({ ...form, items: form.items.filter((_, k) => k !== i) });

  const previewTotals = () => {
    if (!isInvoice) return { total: Number(form.amount || 0) };
    const sub = (form.items || []).reduce((a, it) => a + Number(it.qty || 0) * Number(it.price || 0), 0);
    const tax = sub * Number(form.tax_rate || 0) / 100;
    return { sub, tax, total: sub + tax - Number(form.discount || 0) };
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = isInvoice ? form : { ...form, items: [{ description: form.for_payment, qty: 1, price: Number(form.amount || 0) }], tax_rate: 0, discount: 0 };
      const res = editing ? await api.put(`/admin/${mode}/${editing.id}`, payload) : await api.post(`/admin/${mode}`, payload);
      toast.success(`Tersimpan (No. ${res.data.number})`);
      setOpen(false); load();
    } catch { toast.error("Gagal menyimpan"); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Hapus dokumen ini?")) return; await api.delete(`/admin/${mode}/${id}`); toast.success("Terhapus"); load(); };

  const inp = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-gold text-sm";
  const t = previewTotals();

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-syne font-bold text-3xl text-white">{isInvoice ? "Invoice" : "Kwitansi"}</h1>
          <p className="text-white/50 mt-1">{isInvoice ? "Buat invoice dengan perhitungan otomatis & unduh PDF." : "Buat kwitansi pembayaran & unduh PDF."}</p></div>
        <button onClick={openNew} className="btn-gold px-5 py-2.5 rounded-full flex items-center gap-2" data-testid={`add-${mode}`}><Plus className="w-4 h-4" /> Buat {isInvoice ? "Invoice" : "Kwitansi"}</button>
      </div>

      <div className="space-y-4">
        {items.map((it) => (
          <div key={it.id} className="bg-ll-card rounded-2xl p-5 gold-border flex items-center justify-between gap-4" data-testid={`${mode}-item`}>
            <div className="min-w-0">
              <div className="text-white font-semibold truncate">{isInvoice ? it.client_name : it.received_from}</div>
              <div className="text-white/50 text-xs">No. {it.number} · {it.date}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-gold font-bold font-mono-ll hidden sm:block">{rupiah(it.total)}</span>
              <a href={pdfUrl(`/admin/${mode}/${it.id}/pdf`)} target="_blank" rel="noreferrer" className="btn-gold px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm" data-testid={`${mode}-pdf`}><Download className="w-4 h-4" /> PDF</a>
              <button onClick={() => openEdit(it)} className="bg-white/5 hover:bg-white/10 text-white rounded-lg px-3 py-2"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(it.id)} className="bg-red-500/10 text-red-400 rounded-lg px-3 py-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40">Belum ada dokumen.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0F1117] rounded-2xl gold-border w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-amber-500/15 sticky top-0 bg-[#0F1117]">
              <h3 className="font-syne font-bold text-white text-lg">{editing ? "Edit" : "Buat"} {isInvoice ? "Invoice" : "Kwitansi"}</h3>
              <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {isInvoice ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Nama Klien</label><input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={inp} data-testid="doc-client" /></div>
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Alamat Klien</label><input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className={inp} /></div>
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Tanggal</label><input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} /></div>
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Jatuh Tempo</label><input value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inp} /></div>
                  </div>
                  <div>
                    <label className="text-xs text-white/50 uppercase mb-2 block">Item</label>
                    <div className="space-y-2">
                      {(form.items || []).map((li, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input placeholder="Deskripsi" value={li.description} onChange={(e) => setLine(i, "description", e.target.value)} className={`${inp} flex-1`} data-testid={`doc-item-desc-${i}`} />
                          <input type="number" placeholder="Qty" value={li.qty} onChange={(e) => setLine(i, "qty", Number(e.target.value))} className={`${inp} w-20`} />
                          <input type="number" placeholder="Harga" value={li.price} onChange={(e) => setLine(i, "price", Number(e.target.value))} className={`${inp} w-32`} />
                          <button onClick={() => delLine(i)} className="text-red-400 p-2"><Trash className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                    <button onClick={addLine} className="mt-2 text-gold text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Tambah Item</button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Pajak (%)</label><input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: Number(e.target.value) })} className={inp} /></div>
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Diskon (Rp)</label><input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} className={inp} /></div>
                  </div>
                  <div><label className="text-xs text-white/50 uppercase mb-1 block">Catatan</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} /></div>
                  <div className="bg-white/5 rounded-xl p-4 text-sm text-white/80 space-y-1">
                    <div className="flex justify-between"><span>Subtotal</span><span>{rupiah(t.sub)}</span></div>
                    <div className="flex justify-between"><span>Pajak</span><span>{rupiah(t.tax)}</span></div>
                    <div className="flex justify-between font-bold text-gold border-t border-white/10 pt-1 mt-1"><span>Total</span><span>{rupiah(t.total)}</span></div>
                  </div>
                </>
              ) : (
                <>
                  <div><label className="text-xs text-white/50 uppercase mb-1 block">Telah Terima Dari</label><input value={form.received_from} onChange={(e) => setForm({ ...form, received_from: e.target.value })} className={inp} data-testid="doc-received-from" /></div>
                  <div><label className="text-xs text-white/50 uppercase mb-1 block">Untuk Pembayaran</label><input value={form.for_payment} onChange={(e) => setForm({ ...form, for_payment: e.target.value })} className={inp} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Jumlah (Rp)</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className={inp} data-testid="doc-amount" /></div>
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Tanggal</label><input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} /></div>
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Nama Penerima</label><input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} className={inp} /></div>
                    <div><label className="text-xs text-white/50 uppercase mb-1 block">Jabatan</label><input value={form.signer_title} onChange={(e) => setForm({ ...form, signer_title: e.target.value })} className={inp} /></div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-sm flex justify-between font-bold text-gold"><span>Total</span><span>{rupiah(t.total)}</span></div>
                </>
              )}
              <button onClick={save} disabled={saving} className="btn-gold w-full py-3 rounded-xl disabled:opacity-60" data-testid={`save-${mode}`}>{saving ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
